import { Spinner } from 'construct-ui';
import moment from 'moment';
import MultiSelectComponent from '../../../components/multi-select/multi-select.component';
import paginationComponent from '../../../components/pagination/pagination.component';
import { formats as FORMATS, codeMasterType } from '../../../config/constants';
import { apiStatusCodes } from '../../../config/global-variables';
import codeMasterModel from '../../../models/common/code-master.model';
import WeeklyEmailSummaryModel from '../../../models/monitoring-report/weekly-email-summary/weekly-email-summary.model';
import ViewBase from '../../view-base.view';

export default function WeeklyEmailSummaryView() {
  ViewBase.call(this);

  const DEFAULT_FEILDS = {
    fromDate: moment().subtract(3, 'weeks').format(FORMATS.WEEK_FORMAT),
    toDate: moment().format(FORMATS.WEEK_FORMAT),
    customer: [],
    fileType: [],
    category: [],
  }

  const DEFAULT_PAGINATION = {
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0
  }

  let intfFileTypeOptions = [];
  let customerOptions = [];
  let categoryOptions = [];
  let tableData = [];
  let isLoadingTable = false;
  let isDownloadingFiles = [false, false];
  let previousRequestParams;
  let searchFields = structuredClone(DEFAULT_FEILDS);
  let pagination = structuredClone(DEFAULT_PAGINATION);

  const requestParams = {
    get pageIndex() {
      return pagination.pageIndex;
    },

    get pageSize() {
      return pagination.pageSize;
    },

    get fromDate() {
      if (moment(searchFields.fromDate, FORMATS.WEEK_FORMAT).isValid()) {
        return moment(searchFields.fromDate, FORMATS.WEEK_FORMAT).format(FORMATS.apiDateFormat);
      }

      return '';
    },

    get toDate() {
      if (moment(searchFields.toDate, FORMATS.WEEK_FORMAT).isValid()) {
        return moment(searchFields.toDate, FORMATS.WEEK_FORMAT).endOf('W').format(FORMATS.apiDateFormat);
      }

      return '';
    },

    get customer() {
      return searchFields.customer;
    },

    get fileType() {
      return searchFields.fileType;
    },

    get category() {
      return searchFields.category;
    },
  }

  function resetSearchFields() {
    searchFields = structuredClone(DEFAULT_FEILDS)
  }

  function updateSearchFields(attr, value) {
    if (Array.isArray(searchFields[attr])) {
      searchFields[attr].splice(0, Infinity, ...value);
    } else {
      searchFields[attr] = value;
    }
  }

  function onWeekChanged(e) {
    updateSearchFields(e.target.name, e.target.value);
  }

  let getCustomerOptions = (week) => {
    customerOptions.splice(0);

    return WeeklyEmailSummaryModel.getCustomerList(week)
      .then((result) => {
        if (result?.length > 0) {
          result.map(i => {
            customerOptions.push(i)
          })
        }
      })
      .catch((error) => {
        customerOptions.splice(0);
        this.handleError(error);
      });
  }

  let getIntfFileTypeOptions = () => {
    intfFileTypeOptions.splice(0);

    return WeeklyEmailSummaryModel.getIntfFileTypeList()
      .then((result) => {
        if (result?.length > 0) {
          result.map(i => {
            intfFileTypeOptions.push(i)
          })
        }
      })
      .catch((error) => {
        intfFileTypeOptions.splice(0);
        this.handleError(error);
      });
  }

  let getCategoryOptions = () => {
    categoryOptions.splice(0);
    codeMasterModel.getByCategoryV2({ category: codeMasterType.weeklyEmailSummaryReportCategory })
      .then((result) => {
        result?.map(i => {
          categoryOptions.push({ code: i.code, name: this.getText(i.name, i.name) })
        })
      })
      .catch((error) => {
        categoryOptions.splice(0);
        this.handleError(error);
      })
  }

  let getTableData = () => {
    isLoadingTable = true;

    return WeeklyEmailSummaryModel.searchWeeklyEmailSummary(requestParams)
      .then((result) => {
        if (!result) {
          tableData = [];
          return;
        }

        pagination.totalCount = result.total;
        tableData = result.data;
      })
      .catch((error) => {
        tableData = [];
        this.handleError(error);
      })
      .finally(() => {
        isLoadingTable = false;
      })
  }

  let search = (e) => {
    // Form validation
    if (!this.validateForm(e)) {
      return false;
    }

    Promise.allSettled([getTableData()])
      .then(() => {
        previousRequestParams = structuredClone(requestParams);
      });

    return false;
  }

  let clearFilters = (e) => {
    e.preventDefault();
    let form = document.querySelector('.needs-validation');
    form.classList.remove('was-validated');
    resetSearchFields();
  }

  let getSearchFilters = () => {
    this.showLoading();

    let optionPromises = [
      getCustomerOptions(),
      getIntfFileTypeOptions(),
      getCategoryOptions()
    ];

    Promise.allSettled(optionPromises)
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  let downloadSummary = (e) => {
    e.preventDefault();
    if (isDownloadingFiles[0]) {
      return false;
    }

    isDownloadingFiles[0] = true;
    WeeklyEmailSummaryModel.downloadSummary(previousRequestParams)
      .catch((error) => {
        if (error.code === apiStatusCodes.noContent) {
          this.showErrorMessage(this.getText("LT00000317", "No results found"))
        } else {
          this.handleError(error);
        }
      })
      .finally(() => {
        isDownloadingFiles[0] = false;
      });

    return false;
  }

  let downloadRawData = (e) => {
    e.preventDefault();
    if (isDownloadingFiles[1]) {
      return false;
    }

    isDownloadingFiles[1] = true;
    WeeklyEmailSummaryModel.downloadRawData(previousRequestParams)
      .catch((error) => {
        if (error.code === apiStatusCodes.noContent) {
          this.showErrorMessage(this.getText("LT00000317", "No results found"))
        } else {
          this.handleError(error);
        }
      })
      .finally(() => {
        isDownloadingFiles[1] = false;
      });

    return false;
  }

  let onPageSizeChanged = (pageIndex, pageSize) => {
    pagination.pageIndex = pageIndex;
    pagination.pageSize = pageSize;
    getTableData();
  }

  let onPageChanged = (pageIndex) => {
    pagination.pageIndex = pageIndex;
    getTableData();
  }

  return {
    oninit: (vnode) => {
      getSearchFilters();
    },
    view: (vnode) => {
      return [
        // Search section
        m('.d-flex.flex-column.align-items-end.gap-2.mb-3',
          m('#search-section.card.w-100.mb-0',
            m('.card-body',
              m('form.row.g-2.align-items-end.needs-validation[novalidate]',
                {
                  onsubmit: search
                },
                m('.col-xxl-3.col-xl-4.col-lg-6.col-sm-6.position-relative',
                  m('label.form-label.mandatory[for="period"]', this.getText("LT00000150", "Period")),
                  m('.row.g-1#period',
                    m('.col-6',
                      m('input.form-control.col-6[name="fromDate"][type="week"]', {
                        max: searchFields.toDate,
                        required: true,
                        value: searchFields.fromDate,
                        onchange: onWeekChanged,
                      }),
                      m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
                    ),
                    m('.col-6',
                      m('input.form-control[name="toDate"][type="week"]', {
                        min: searchFields.fromDate,
                        required: true,
                        value: searchFields.toDate,
                        onchange: onWeekChanged
                      }),
                      m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
                    )
                  ),
                ),
                m('.col-xxl-3.col-xl-4.col-lg-6.col-sm-6',
                  m('label.form-label[for=customer]', this.getText("LT00008046", "Customer")),
                  m(MultiSelectComponent, {
                    id: 'customer',
                    keyOnly: true,
                    items: customerOptions,
                    selectedItems: searchFields.customer,
                    onSelect: (selectedItems) => {
                      updateSearchFields('customer', selectedItems);
                    }
                  }),
                ),
                m('.col-xxl-3.col-xl-4.col-lg-6.col-sm-6',
                  m('label.form-label[for=fileType]', this.getText("LT00008055", "File Type")),
                  m(MultiSelectComponent, {
                    id: 'fileType',
                    keyOnly: true,
                    items: intfFileTypeOptions,
                    selectedItems: searchFields.fileType,
                    onSelect: (selectedItems) => {
                      updateSearchFields('fileType', selectedItems);
                    }
                  }),
                ),
                m('.col-xxl-3.col-xl-4.col-lg-6.col-sm-6',
                  m('label.form-label[for=category]', this.getText("LT00000366", "Category")),
                  m(MultiSelectComponent, {
                    id: 'category',
                    keyOnly: true,
                    items: categoryOptions,
                    selectedItems: searchFields.category,
                    onSelect: (selectedItems) => {
                      updateSearchFields('category', selectedItems);
                    }
                  }),
                ),

                // Search & clear buttons
                m('.col-12.d-flex.justify-content-end.gap-2',
                  m('button.btn.btn-primary[type="submit"]', {
                  }, this.getText("LT00000001", "Search")),
                  m('button.btn.btn-white', {
                    onclick: clearFilters
                  }, this.getText("LT00000031", "Clear")),
                ),
              ),
            )
          ),
        ),
        // Search section - end

        // Table section
        m('.card',
          m('.card-body',
            m(Spinner, {
              active: isLoadingTable,
              background: true,
              fill: true,
              size: 'xl',
            }),
            m('.d-flex.justify-content-end.mb-2',
              m('.input-group.w-auto',
                m('button.btn.btn-primary.dropdown-toggle.dropdown-toggle-split[type=button][data-bs-toggle=dropdown][arie-expanded=false]',
                  {
                    disabled: !tableData.length,
                  },
                  isDownloadingFiles.includes(true)
                    ? m('span.spinner-border.spinner-border-sm.me-2[role=status][aria-hidden=true]')
                    : m('i.bi.bi-file-earmark-arrow-down-fill'),
                  this.getText("LT00000492", "Download")
                ),
                m('ul.dropdown-menu',
                  m('li',
                    m('a.dropdown-item[href="javascript:void(0);"]', {
                      class: isDownloadingFiles[0] ? 'disabled' : '',
                      onclick: downloadSummary
                    },
                      isDownloadingFiles[0] &&
                      m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                      this.getText("LT00001095", "Summary")
                    )
                  ),
                  m('li',
                    m('a.dropdown-item[href="javascript:void(0);"]', {
                      class: isDownloadingFiles[1] ? 'disabled' : '',
                      onclick: downloadRawData
                    },
                      isDownloadingFiles[1] &&
                      m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                      this.getText("LT00008050", "Raw Data")
                    )
                  ),
                ),
              ),
            ),
            m('.row',
              m('.col-12.p-0',
                m('.table-responsive',
                  m('table.table', [
                    m('thead.thead-bggrey',
                      m('tr.align-middle', [
                        m('th[scope="col"]', this.getText("LT00008046", "Customer")),
                        m('th[scope="col"]', this.getText("LT00000337", "Week")),
                        m('th[scope="col"]', this.getText("LT00001096", "Day Range (Start - End)")),
                        m('th[scope="col"]', this.getText("LT00000013", "Data Type")),
                        m('th[scope="col"]', this.getText("LT00000366", "Category")),
                        m('th[scope="col"]', this.getText("LT00001097", "Error Type")),
                        m('th[scope="col"]', this.getText("LT00001098", "Value")),
                        m('th[scope="col"]', this.getText("LT00000152", "Remarks")),
                      ])
                    ),
                    m('tbody',
                      (tableData.length == 0) &&
                      m('tr.align-middle',
                        m('td[colspan=8]', this.getText("EM00000141", "No records found."))
                      ),
                      tableData.length > 0 &&
                      tableData.map((col, index) => [
                        m('tr.align-middle',
                          m('td', col.customerName),
                          m('td', col.week),
                          m('td', col.dayRange),
                          m('td', col.dataType),
                          m('td', col.category),
                          m('td', col.errorType),
                          m('td', col.value),
                          m('td', col.remarks)
                        )
                      ])
                    )
                  ])
                ),
                tableData.length > 0 &&
                m(".px-3",
                  m(paginationComponent, {
                    currentPage: pagination.pageIndex,
                    pageSize: pagination.pageSize,
                    totalItems: pagination.totalCount,
                    onPageChanged: onPageChanged,
                    onPageSizeChanged: onPageSizeChanged
                  })
                )
              )
            )
          )
        ),
        // Table section - end

        this.getBaseViews()
      ]
    }
  }
}
